import {
  Box,
  Card,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import FormHeaderGreeting from 'src/components/Greeting/FormHeaderGreeting';
import { RootState } from 'src/redux-store/appStore';
import { httpRequest } from 'src/services/httpRequest';
import {
  PaymentApplicationDetails,
  PaymentSummary
} from 'src/types/RoadPermit/roadPermit.type';
import CustomModal from 'src/ui-component/customModal/CustomModal';
import FormikForm from 'src/ui-component/FormikFormLibrary';
import { FormikRadioButton } from 'src/ui-component/FormikFormLibrary/FormikRadioButton';
import { Fields } from 'src/ui-component/FormikFormLibrary/FormikTypes';
import { getFeesForCurrentPermit } from '../common/getFeesForCurrentPermit';
import { handleError } from 'src/redux-store/slices/errorSlice';
import { Container } from '@mui/system';
import Breadcrumb from 'src/ui-component/Breadcrumb';
import DownloadPopUp from 'src/ui-component/DownloadApplicationForm/DownloadPopUp';

interface SitePlanTypes {
  selectedPlanOpt: string;
  blockLotDetails: { NeedSitePlan: string };
}

function TreePermitApplication() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { townshipId, permit } = useParams();
  const userData = useSelector((store: RootState) => store.user.userDetails);
  const [loading, setLoading] = useState<boolean>(false);
  const [clickedOn, setClickedOn] = useState<string>('');
  const [treePermitFormData, setTreePermitFormData] = useState<any>({});
  const [blockLotsOptions, setBlockLotsOptions] = useState<string[]>([]);
  const [error, setError] = useState<string>();
  const [IsOpen, setIsOpen] = useState<boolean>(false);
  const [feeDetails, setFeeDetails] = useState({
    applicationFee: 0,
    inspectionFee: 0
  });
  const [sitePlan, setSitePlan] = useState<SitePlanTypes>();
  const [taxRate, setTaxRate] = useState<number>(0);

  // for get application fee
  const handleGetApplicationFee = async () => {
    setLoading(true);
    const response = await httpRequest(
      'GET',
      `/configuration/fee_details?PermitID=${permit}`
    );
    if (response?.status === 'success') {
      const feeObject = getFeesForCurrentPermit(response.data);
      setTaxRate(feeObject?.CreditCardFee || 0);
      setFeeDetails({
        applicationFee: Number(feeObject?.ApplicationFee),
        inspectionFee: Number(feeObject?.InspectionFee)
      });
      setLoading(false);
    } else if (response?.type === 'NETWORK_ERROR') {
      setLoading(false);
    } else {
      setLoading(false);
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  useEffect(() => {
    if (permit) {
      handleGetApplicationFee();
    }
  }, []);

  // for get site plan details
  const handleGetSitePlanDetails = async (blockLot) => {
    const response = await httpRequest(
      'GET',
      `/propertyDetails/searchBlockLots_Address?BlockLotPrefix=${blockLot}`
    );
    if (response?.status === 'success') {
      setSitePlan({ ...sitePlan, blockLotDetails: response?.data[0] });
    } else if (response?.type === 'NETWORK_ERROR') {
    } else {
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  // for get block lots list
  const handleBlockLotsList = async (val) => {
    if (val === undefined) setTreePermitFormData({});
    if (val) {
      if (val?.BlockLot) {
        handleGetSitePlanDetails(val?.BlockLot);
        setTreePermitFormData({
          ...treePermitFormData,
          blockLot: val?.BlockLot,
          owner: val?.Owner,
          treeAddress: val?.PropAddress
        });
      } else {
        setTreePermitFormData({ ...treePermitFormData, blockLot: val });
        if (val?.length > 2) {
          const response = await httpRequest(
            'GET',
            `/propertyDetails/searchBlockLots_Address?BlockLotPrefix=${val}`
          );
          if (response?.status === 'success') {
            const list = response?.data
              ?.map((value: { BlockLot: string }) => {
                return { ...value, label: value?.BlockLot };
              })
              ?.slice(0, 10);
            setBlockLotsOptions(list);
          } else if (response?.type === 'NETWORK_ERROR') {
          } else {
            toast.error(response?.message);
            dispatch(handleError(response?.message));
          }
        }
      }
    }
  };

  // for upload map file
  const handleFileUpload = async (file) => {
    if (file.length) {
      const formDataToSend = new FormData();
      formDataToSend.append('uploadPDF', file![0]);

      const result = await httpRequest(
        'POST',
        '/uploadPDF/?permitName=Tree',
        formDataToSend,
        {
          'Content-Type': 'multipart/form-data'
        }
      );
      if (result?.status === 'success') {
        setTreePermitFormData({
          ...treePermitFormData,
          DocumentFileUpload: result.data.fileName,
          mapFile: file
        });
      } else if (result?.type === 'NETWORK_ERROR') {
      } else {
        toast.error(result?.message);
        dispatch(handleError(result?.message));
      }
    } else {
      setTreePermitFormData({
        ...treePermitFormData,
        DocumentFileUpload: '',
        mapFile: []
      });
    }
  };

  // for handle change
  const handleChange = (val, e) => {
    const { name } = e;
    if (name === 'blockLot') handleBlockLotsList(val);
    else
      setTreePermitFormData({
        ...treePermitFormData,
        [name]: val
      });
  };

  // for proceed to pay
  const handleProceedToPay = async () => {
    setLoading(true);
    const data = treePermitFormData;
    const payload = {
      BlockLots: data?.blockLot,
      OwnerName: data?.owner,
      OwnerAddress: data?.treeAddress,
      NearestCrossStreet: data?.nearBy,
      OwnerPhoneNumber: data?.phone,
      NoOfTrees: Number(data?.numberOfTrees),
      RemovalPurpose: data?.purpose,
      LotSize: Number(data?.lotSize),
      TreesInRestrictedArea: data?.areaConfirmationCheck,
      ApplicationFee: feeDetails?.applicationFee || 0,
      SitePlanApproval: sitePlan?.selectedPlanOpt || null,
      DocumentFileUpload: treePermitFormData?.DocumentFileUpload
    };
    const response = await httpRequest(
      'POST',
      `/tree_permit_application/insert`,
      payload
    );
    if (response?.status === 'success') {
      toast.success('Application submitted successfully.');
      const paymentSummary: PaymentSummary = {
        fees: Object.fromEntries(
          Object.entries(feeDetails).filter(([, value]) => value > 0)
        ),
        ...(taxRate > 0 ? { taxRate } : {})
      };
      const paymentApplicationDetails: PaymentApplicationDetails = {
        applicationDetails: {
          userName: `${userData?.FirstName} ${userData?.LastName}`,
          applicationNumber: null,
          applicationId: response?.data?.ApplicationID,
          applicationStatus: 'Draft',
          statusPayload: 'Await CO',
          permitPrefix: 'Tree'
        }
      };
      navigate(`/payment-methods`, {
        state: { paymentSummary, paymentApplicationDetails }
      });
    } else if (response?.type === 'NETWORK_ERROR') {
    } else {
      setLoading(false);
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  // for insert application
  const handleInsertApplication = async (params) => {
    setLoading(true);
    const data = { ...treePermitFormData, ...params };
    const payload = {
      BlockLots: data?.blockLot,
      OwnerName: data?.owner,
      OwnerAddress: data?.treeAddress,
      NearestCrossStreet: data?.nearBy,
      OwnerPhoneNumber: data?.phone,
      NoOfTrees: data?.numberOfTrees,
      RemovalPurpose: data?.purpose,
      LotSize: data?.lotSize,
      TreesInRestrictedArea: data?.areaConfirmationCheck,
      ApplicationFee: feeDetails?.applicationFee || 0,
      SitePlanApproval: sitePlan?.selectedPlanOpt || null,
      DocumentFileUpload: treePermitFormData?.DocumentFileUpload
    };
    const response = await httpRequest(
      'POST',
      `/tree_permit_application/insert`,
      payload
    );
    if (response?.status === 'success') {
      toast.success('Application submitted successfully.');
      if (clickedOn === 'Save Draft') {
        setLoading(false);
        navigate(
          `/${townshipId}/Engineering Department/${permit}/draft-application`
        );
      }
    } else if (response?.type === 'NETWORK_ERROR') {
    } else {
      setLoading(false);
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  // for handle submit
  const handleSubmit = async (data) => {
    if (
      treePermitFormData?.code457Check &&
      treePermitFormData?.TRContractorCheck &&
      treePermitFormData?.confirmApplicationCheck
    ) {
      if (clickedOn === 'Proceed to Payment') {
        setIsOpen(true);
      } else {
        await handleInsertApplication(data);
      }
    } else {
      setError('Please select all checkboxes to continue.');
    }
  };

  //form fieldsusing formik
  const formFields: Fields[] = [
    {
      fieldName: 'blockLot',
      fieldLabel: 'Block Lot OR Address where tree(s) are located ',
      fieldType: 'autocomplete',
      placeHolder: 'Enter minimum 3 digits',
      isErrorField: false,
      required: true,
      initialValue: treePermitFormData?.blockLot,
      disabled: false,
      options: blockLotsOptions || [{ label: treePermitFormData?.blockLot }],
      dependency: handleChange
    },
    {
      fieldName: 'owner',
      fieldLabel: 'Owner ',
      fieldType: 'text',
      isErrorField: false,
      required: true,
      initialValue: treePermitFormData?.owner?.trim(),
      disabled: false,
      dependency: handleChange
    },
    {
      fieldName: 'treeAddress',
      fieldLabel: 'Address where tree(s) are located ',
      fieldType: 'note',
      isErrorField: false,
      required: true,
      initialValue: treePermitFormData?.treeAddress?.trim(),
      disabled: false,
      dependency: handleChange
    },
    {
      fieldName: 'nearBy',
      fieldLabel: 'Nearest Cross Street ',
      fieldType: 'note',
      isErrorField: false,
      required: true,
      initialValue: treePermitFormData?.nearBy?.trim(),
      disabled: false,
      dependency: handleChange
    },
    {
      fieldName: 'phone',
      fieldLabel: 'Phone Number ',
      fieldType: 'mobile',
      isErrorField: false,
      required: true,
      initialValue: treePermitFormData?.phone,
      disabled: false,
      dependency: handleChange
    },
    {
      fieldName: 'numberOfTrees',
      fieldLabel: 'Number of Trees to be Removed',
      fieldType: 'number',
      isErrorField: false,
      required: true,
      initialValue: treePermitFormData?.numberOfTrees,
      lengthLimit: 3,
      disabled: false,
      dependency: handleChange
    },
    {
      fieldName: 'purpose',
      fieldLabel: 'Purpose of Removal ',
      fieldType: 'note',
      isErrorField: false,
      required: true,
      initialValue: treePermitFormData?.purpose?.trim(),
      disabled: false,
      dependency: handleChange
    },
    {
      fieldName: 'lotSize',
      fieldLabel: 'Lot Size (S.F./ Acreage) ',
      fieldType: 'number',
      isErrorField: false,
      required: true,
      initialValue: treePermitFormData?.lotSize?.trim(),
      disabled: false,
      dependency: handleChange
    },
    {
      fieldName: 'areaConfirmationCheck',
      fieldLabel:
        'Are trees being removed from a restricted area? (i.e. Wetlands, Easements, Buffers, etc.)',
      fieldType: 'radio',
      isErrorField: false,
      required: true,
      initialValue: treePermitFormData?.areaConfirmationCheck,
      options: [
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
        { value: 'Unsure', label: 'Unsure' }
      ],
      disabled: false,
      dependency: handleChange
    },
    {
      fieldName: 'mapFile',
      fieldLabel: 'Upload map',
      fieldType: 'file',
      isErrorField: false,
      required: true,
      listUI: true,
      placement: 'start',
      initialValue: treePermitFormData?.mapFile,
      multiSelect: false,
      disabled: false,
      aboveParagraph: `Please attach a copy of your survey, property plot plan, or hand drawing showing the location of the tree(s) to be removed, 
        all structures on the property and any easements. Please mark each tree to be removed with an "X". 
        Only image files (JPG, PNG, GIF, SVG, HEIC) and PDF files are allowed, with maximum file size of 10MB.`,
      belowParagraph:
        'Photos of the tree(s) to be removed and aerial images are not accepted. doing so may delay the processing of your application.',
      dependency: handleFileUpload
    }
  ];

  const TreePermitBottomSection = () => {
    const treePermitApplicationChecks = [
      {
        name: 'code457Check',
        label: (
          <Typography>
            I have read and understand Township Code Chapter 457
            <a
              href="https://ecode360.com/7014056"
              target="_blank"
              rel="noreferrer"
            >
              {' '}
              Trees and Invasive Species
            </a>
            .
          </Typography>
        )
      },
      {
        name: 'TRContractorCheck',
        label:
          'I understand that the Tree Removal Contractor I will retain is registered.'
      },
      {
        name: 'confirmApplicationCheck',
        label: (
          <>
            <b>
              All trees to be removed must be field marked prior to submitting
              your application.{' '}
            </b>
            An example would be tying string around the tree(s), rope, etc.
            Please be advised that if trees are not field marked, the
            application may be denied.
          </>
        )
      }
    ];
    return (
      <>
        {sitePlan?.blockLotDetails?.NeedSitePlan === 'yes' && (
          <FormikRadioButton
            disabled={false}
            label={'Do you  have preliminary or final site plan approval?'}
            fieldValue={{ value: sitePlan?.selectedPlanOpt }}
            options={[
              { value: 'Yes', label: 'Yes' },
              { value: 'No', label: 'No' },
              { value: 'Unsure', label: 'Unsure' },
              { value: 'Not Required', label: 'Not Required' }
            ]}
            handleChange={(e) =>
              setSitePlan({ ...sitePlan, selectedPlanOpt: e.target.value })
            }
          />
        )}
        <TextField
          id="outlined-basic"
          value={`$${feeDetails?.applicationFee}.00`}
          disabled
          sx={{ mt: 2 }}
          label="Application Fee *"
          variant="outlined"
          InputLabelProps={{
            className: 'weight-600'
          }}
        />
        {treePermitApplicationChecks?.map((data, i) => (
          <Box mt={i === 0 && 2} key={i}>
            <Box mt={{ xs: 1.5, sm: 0 }}>
              <FormControlLabel
                sx={{ alignItems: 'start' }}
                control={
                  <Checkbox
                    name={data?.name}
                    checked={treePermitFormData[data?.name]}
                    color="primary"
                    onChange={(e) => {
                      setTreePermitFormData({
                        ...treePermitFormData,
                        [data.name]: e.target.checked
                      });
                      setError('');
                    }}
                  />
                }
                label={data?.label}
              />
            </Box>
          </Box>
        ))}
        <Typography color={'#8B0000'} mt={2}>
          {error}
        </Typography>
      </>
    );
  };

  return loading ? (
    <LoadingOverlay />
  ) : (
    <Container sx={{ mt: 2 }} className="main-table-container">
      <Breadcrumb PermitName={'Tree Removal'} PageTitle={'New Application'} />
      <Grid item md={3} sm={3} p={{ xs: '0 !important', sm: 'auto' }}>
        <FormHeaderGreeting
          name={`${userData?.FirstName} ${userData?.LastName}`}
          applicationType="new"
        />
        <Card sx={{ p: { xs: 1, sm: 3 } }}>
          <Box
            mb={2}
            sx={{
              display: 'flex',
              justifyContent: 'flex-end'
            }}
          >
            <DownloadPopUp
              title={'Tree Removal Application'}
              ApplicationDetails={{
                BlockLot: '',
                FullName: '',
                AddressWhereTreeAreLocated: '',
                NearestCrossStreet: '',
                phoneNumber: '',
                NumberOfTreesToBeRemoved: '',
                PurposeOfRemoval: '',
                'Lot Size (S.F./ Acreage)': '',
                'Are trees being removed from a restricted area? (i.e. Wetlands, Easements, Buffers, etc.)':
                  'Yes / No / Unsure',
                'I have read and understand Township Code Chapter 457 Trees and Invasive Species.':
                  <Checkbox />,
                'I understand that the Tree Removal Contractor I will retain is registered.':
                  <Checkbox />,
                'All trees to be removed must be field marked prior to submitting your application.':
                  <Checkbox />
              }}
            />
          </Box>
          <Box sx={{ mt: 1 }} className="newApp tree_permit_new_app">
            <FormikForm
              allFields={formFields}
              buttonTitle={['Cancel', 'Save Draft', 'Proceed to Payment']}
              setClickedOn={setClickedOn}
              clickProp
              formMethod={handleSubmit}
              content={TreePermitBottomSection}
              buttonPlacement={'right'}
              handleOtherButton={() =>
                navigate(
                  `/${townshipId}/Engineering Department/${permit}/application-list`
                )
              }
            />
          </Box>
        </Card>
        <CustomModal
          title={'Refund Policy'}
          IsOpen={IsOpen}
          setIsOpen={setIsOpen}
          loading={loading}
          onSave={handleProceedToPay}
          actionButtons={['Cancel ', 'Continue']}
          content={
            <Typography>
              Please be advised that this application fee is only refundable
              within the first 48 hours from submitting your payment. You will
              still be able to withdraw your application after the refund period
              expires but no refund will be issued.
            </Typography>
          }
        />
      </Grid>
    </Container>
  );
}

export default TreePermitApplication;
